import type { ApiResponse } from "apisauce"

import { request } from "../../../../../services/api"
import { unwrapEnvelope } from "../../../../../utils/api/unwrap-envelope.ts"
import type {
  AddressDecision,
  AddressMatch,
  AnalysisDetail,
  AnalysisOptions,
  AnalysisRow,
  AuditEvent,
  BulkDeleteResult,
  ClientState,
  PlanCreator,
  FileActivity,
  LinkableRecord,
  LinkDraft,
  PageText,
  Paged,
  PlanActionType,
  ProjectTypeOption,
  RecordFile,
  SubcontractorRef,
  SubcontractPaymentPatch,
  SubcontractsState,
  VersionDetail,
  VersionListRow
} from "./types.ts"

const BASE_URL = "/sow-receivables"
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } }

// Our own refusals arrive bare as `{code, message, details, field_errors}`; a
// framework refusal (403/404 from DRF) arrives as `{message: {detail}, details: {message}}`.
interface ErrorBody {
  code?: string
  message?: string | { detail?: string }
  detail?: string
  details?: Record<string, unknown> & { message?: string; code?: string }
  field_errors?: { path: string; message: string }[]
}

export class SowApiError extends Error {
  constructor(
    message: string,
    public code: string | undefined,
    public status: number | undefined,
    public details: Record<string, unknown> = {},
    public fieldErrors: { path: string; message: string }[] = []
  ) {
    super(message)
    this.name = "SowApiError"
  }
}

export function toSowError(
  response: ApiResponse<unknown, unknown>,
  fallback: string
): SowApiError {
  const body = (response.data ?? {}) as ErrorBody
  // A server fault with no code of ours says nothing written for a customer.
  const fault = (response.status ?? 0) >= 500 && !body.code
  const message =
    (!fault &&
      ((typeof body.message === "string"
        ? body.message
        : body.message?.detail) ||
        body.detail ||
        (typeof body.details?.message === "string"
          ? body.details.message
          : undefined))) ||
    fallback
  return new SowApiError(
    message,
    body.code ||
      (typeof body.details?.code === "string" ? body.details.code : undefined),
    response.status,
    (body.details as Record<string, unknown>) || {},
    body.field_errors || []
  )
}

async function unwrap<T>(
  promise: Promise<ApiResponse<unknown, unknown>>,
  fallback: string
): Promise<T> {
  const response = await promise
  if (response.ok) {
    return response.data === undefined || response.data === null
      ? (undefined as T)
      : unwrapEnvelope<T>(response.data)
  }
  throw toSowError(response, fallback)
}

const path = (id: string, rest = "") => `${BASE_URL}/${id}/${rest}`
// The revision travels as a query parameter: the API's CORS policy does not allow a
// cross-origin If-Match header.
const ifMatch = (revision?: number) =>
  revision === undefined ? {} : { params: { revision } }
const withRevision = (params: Record<string, unknown>, revision?: number) =>
  revision === undefined ? params : { ...params, revision }

export interface ListParams {
  q?: string
  status?: string
  target_kind?: string
  /** Analyses of this lead or project. */
  target?: string
  /** Analyses that read this stored document / album file. */
  document?: string
  file?: string
  /** Comma-separated: lead, project, unlinked, new_record. */
  destination?: string
  /** A user id, or "me". */
  created_by?: string
  created_from?: string
  created_to?: string
  updated_from?: string
  updated_to?: string
  /** name, created, updated, status or progress; "-" for descending. */
  ordering?: string
  page?: number
  page_size?: number
}

export const listAnalyses = (params: ListParams = {}) =>
  unwrap<Paged<AnalysisRow>>(
    request.api.get(`${BASE_URL}/`, params),
    "Could not load analyses"
  )

export const createAnalysis = (body: {
  name?: string
  target?: string | null
  options?: Partial<AnalysisOptions>
  /** "change_request": read for a project change request, never applied by this tool's plan. */
  purpose?: "change_request"
  project_types?: string[]
  /** Started from a record (or a form on one): its destination can't change. */
  lock_target?: boolean
  /** Started from Add Milestone: its project type can't change either. */
  lock_project_types?: boolean
}) =>
  unwrap<AnalysisDetail>(
    request.api.post(`${BASE_URL}/`, body),
    "Could not start an analysis"
  )

export const getAnalysis = (id: string) =>
  unwrap<AnalysisDetail>(
    request.api.get(path(id)),
    "Could not load the analysis"
  )

export const updateContext = (
  id: string,
  body: Partial<{
    name: string
    target: string | null
    project_types: string[]
    context: string
    options: AnalysisOptions
    destination_intent: "new_project" | "new_lead" | null
  }>,
  revision?: number
) =>
  unwrap<AnalysisDetail>(
    request.api.patch(path(id), body, ifMatch(revision)),
    "Could not save"
  )

export const deleteAnalysis = (
  id: string,
  options: { discard?: boolean } = {}
) =>
  unwrap<void>(
    request.api.delete(path(id), options.discard ? { discard: 1 } : {}),
    "Could not delete"
  )

/** Deletes the chosen plans, or every plan the list's filters match; each refusal
 *  comes back with its reason. Files and records are never deleted. */
export const bulkDeleteAnalyses = (
  body: { ids: string[] } | { all: true; filters: ListParams }
) =>
  unwrap<BulkDeleteResult>(
    request.api.post(`${BASE_URL}/bulk-delete/`, body),
    "Could not delete the plans"
  )

export const listCreators = () =>
  unwrap<{ results: PlanCreator[]; can_filter_others: boolean }>(
    request.api.get(`${BASE_URL}/creators/`),
    "Could not load who created the plans"
  )

/** Save draft: keeps this version as the one Discard returns to. */
export const saveDraft = (id: string, clientState: ClientState) =>
  unwrap<AnalysisDetail>(
    request.api.post(path(id, "save-draft/"), { client_state: clientState }),
    "Could not save the draft"
  )

/** Back to the last Save draft; answers with the wizard state saved with it. */
export const discardChanges = (id: string) =>
  unwrap<AnalysisDetail & { client_state: ClientState }>(
    request.api.post(path(id, "discard-changes/"), {}),
    "Could not discard the changes"
  )

/** Records the document address the user confirmed (or could not), and for a linked
 *  record which address it keeps. Nothing on the record changes until the plan is applied. */
export const reviewAddress = (
  id: string,
  body: {
    status: "confirmed" | "unresolved"
    match?: AddressMatch | null
    decision?: AddressDecision | null
  },
  revision?: number
) =>
  unwrap<AnalysisDetail>(
    request.api.post(path(id, "address-review/"), body, ifMatch(revision)),
    "Could not save the address"
  )

export const reconcile = (
  id: string,
  body:
    | { method: "payments" }
    | { method: "contract"; contract_total: string }
    | {
        method: "balance"
        label: string
        amount: string
        trigger_type: string
        trigger_text?: string
      },
  revision?: number
) =>
  unwrap<AnalysisDetail>(
    request.api.post(path(id, "reconcile/"), body, ifMatch(revision)),
    "Could not resolve the difference"
  )

/** Uses a file already stored on a lead, project or the company — linked, not copied. */
export const linkStoredFile = (
  id: string,
  stored: { source: "document" | "file"; id: string },
  role: "primary" | "supporting"
) =>
  unwrap<AnalysisDetail>(
    request.api.post(
      path(id, "files/"),
      stored.source === "document"
        ? { document: stored.id, role }
        : { file_id: stored.id, role }
    ),
    "Could not add the file"
  )

export const listRecordFiles = (id: string) =>
  unwrap<{ results: RecordFile[] }>(
    request.api.get(path(id, "record-files/")),
    "Could not load the record's files"
  )

export const getFileActivity = (params: {
  documents?: string[]
  files?: string[]
}) =>
  unwrap<FileActivity>(
    request.api.get(`${BASE_URL}/file-activity/`, {
      documents: (params.documents ?? []).join(","),
      files: (params.files ?? []).join(",")
    }),
    "Could not load AI activity"
  )

export const uploadFile = (
  id: string,
  file: File,
  role: "primary" | "supporting"
) => {
  const data = new FormData()
  data.append("file", file)
  data.append("role", role)
  return unwrap<AnalysisDetail>(
    request.api.post(path(id, "files/"), data, MULTIPART),
    "Upload failed"
  )
}

export const deleteFile = (id: string, fileId: string) =>
  unwrap<AnalysisDetail>(
    request.api.delete(path(id, `files/${fileId}/`)),
    "Could not remove the file"
  )

export const startRun = (
  id: string,
  body: {
    idempotencyKey: string
    scope?: "all" | "milestones" | "receivables"
    instructions?: string
  }
) =>
  unwrap<AnalysisDetail>(
    request.api.post(path(id, "runs/"), {
      idempotency_key: body.idempotencyKey,
      scope: body.scope ?? "all",
      instructions: body.instructions ?? ""
    }),
    "Could not start the analysis"
  )

// Opens the applied plan again as an editable copy, to put it on the project again.
export const realignPlan = (id: string, revision?: number) =>
  unwrap<AnalysisDetail>(
    request.api.post(path(id, "realign/"), {}, ifMatch(revision)),
    "Could not reopen the plan"
  )

export const cancelRun = (id: string) =>
  unwrap<AnalysisDetail>(
    request.api.post(path(id, "cancel/")),
    "Could not cancel"
  )

export const listVersions = (id: string) =>
  unwrap<VersionListRow[]>(
    request.api.get(path(id, "versions/")),
    "Could not load versions"
  )

export const getVersion = (id: string, number: number) =>
  unwrap<VersionDetail>(
    request.api.get(path(id, `versions/${number}/`)),
    "Could not load the version"
  )

const edit = (promise: Promise<ApiResponse<unknown, unknown>>) =>
  unwrap<AnalysisDetail>(promise, "Could not save")

export const patchMilestone = (
  id: string,
  key: string,
  body: Record<string, unknown>,
  revision?: number
) =>
  edit(
    request.api.patch(path(id, `milestones/${key}/`), body, ifMatch(revision))
  )
export const addMilestone = (
  id: string,
  body: Record<string, unknown>,
  revision?: number
) => edit(request.api.post(path(id, "milestones/"), body, ifMatch(revision)))
export const deleteMilestone = (
  id: string,
  key: string,
  dropLinks = false,
  revision?: number
) =>
  edit(
    request.api.delete(
      path(id, `milestones/${key}/`),
      withRevision(dropLinks ? { drop_links: "true" } : {}, revision)
    )
  )
export const mergeMilestones = (
  id: string,
  keys: string[],
  survivor: { name?: string; duration_days?: number | null },
  revision?: number
) =>
  edit(
    request.api.post(
      path(id, "milestones/merge/"),
      { keys, survivor },
      ifMatch(revision)
    )
  )
export const splitMilestone = (
  id: string,
  key: string,
  parts: { name: string; scope_keys: string[] }[],
  revision?: number
) =>
  edit(
    request.api.post(
      path(id, "milestones/split/"),
      { key, parts },
      ifMatch(revision)
    )
  )
export const reorderMilestones = (
  id: string,
  keys: string[],
  revision?: number
) =>
  edit(
    request.api.post(
      path(id, "milestones/reorder/"),
      { keys },
      ifMatch(revision)
    )
  )

export const patchReceivable = (
  id: string,
  key: string,
  body: Record<string, unknown>,
  revision?: number
) =>
  edit(
    request.api.patch(path(id, `receivables/${key}/`), body, ifMatch(revision))
  )
export const addReceivable = (
  id: string,
  body: Record<string, unknown>,
  revision?: number
) => edit(request.api.post(path(id, "receivables/"), body, ifMatch(revision)))
export const deleteReceivable = (id: string, key: string, revision?: number) =>
  edit(
    request.api.delete(
      path(id, `receivables/${key}/`),
      withRevision({}, revision)
    )
  )
export const addBalance = (id: string, revision?: number) =>
  edit(
    request.api.post(
      path(id, "receivables/add-balance/"),
      {},
      ifMatch(revision)
    )
  )

export const replaceLinks = (
  id: string,
  links: LinkDraft[],
  revision?: number
) => edit(request.api.put(path(id, "links/"), { links }, ifMatch(revision)))
export const suggestLinks = (id: string) =>
  unwrap<{ add: LinkDraft[]; remove: LinkDraft[] }>(
    request.api.post(path(id, "links/suggest/")),
    "Could not suggest links"
  )

export type TriggerAnswer =
  | { kind: "milestone"; milestone_keys: string[]; condition: string }
  | { kind: "on_signature" }
  | { kind: "fixed_date"; due_date: string }
  | { kind: "later" }

// One answer to "when is this payment due?": the trigger and its link change together.
export const answerTrigger = (
  id: string,
  key: string,
  body: TriggerAnswer,
  revision?: number
) =>
  edit(
    request.api.post(
      path(id, `receivables/${key}/trigger/`),
      body,
      ifMatch(revision)
    )
  )

export const chooseContractValue = (
  id: string,
  amount: string,
  revision?: number
) =>
  edit(
    request.api.post(path(id, "contract-value/"), { amount }, ifMatch(revision))
  )

export const revalidate = (id: string, revision?: number) =>
  edit(request.api.post(path(id, "validate/"), {}, ifMatch(revision)))

export const acknowledge = (id: string, code: string, revision?: number) =>
  edit(request.api.post(path(id, "acknowledge/"), { code }, ifMatch(revision)))
export const setNotApplicable = (
  id: string,
  phaseId: string,
  reason: string | null,
  revision?: number
) =>
  edit(
    request.api.post(
      path(id, "not-applicable/"),
      { phase_id: phaseId, reason },
      ifMatch(revision)
    )
  )

export const buildPlan = (
  id: string,
  revision?: number,
  mode?: "merge" | "replace"
) =>
  edit(
    request.api.post(path(id, "plan/"), mode ? { mode } : {}, ifMatch(revision))
  )
export const changeAction = (
  id: string,
  actionId: string,
  body: {
    action: PlanActionType
    target_record_id?: string | null
    reason?: string
  },
  revision?: number
) =>
  edit(
    request.api.patch(
      path(id, `plan/actions/${actionId}/`),
      body,
      ifMatch(revision)
    )
  )
export const submitPlan = (id: string, revision?: number) =>
  edit(request.api.post(path(id, "plan/submit/"), {}, ifMatch(revision)))
export const approvePlan = (
  id: string,
  body: { idempotencyKey: string; note: string; acknowledged: boolean }
) =>
  unwrap<AnalysisDetail>(
    request.api.post(path(id, "plan/approve/"), {
      idempotency_key: body.idempotencyKey,
      note: body.note,
      acknowledged: body.acknowledged
    }),
    "Could not apply the plan"
  )

export const listEvents = (id: string, page = 1) =>
  unwrap<Paged<AuditEvent>>(
    request.api.get(path(id, "events/"), { page }),
    "Could not load history"
  )

export const getPage = (id: string, pageNo: number) =>
  unwrap<PageText>(
    request.api.get(path(id, `pages/${pageNo}/`)),
    "Could not load the page"
  )

export async function getPageImage(id: string, pageNo: number): Promise<Blob> {
  const response = await request.api.get<Blob, unknown>(
    path(id, `pages/${pageNo}/image/`),
    {},
    { responseType: "blob" }
  )
  if (response.ok && response.data instanceof Blob) return response.data
  throw new SowApiError("Could not draw the page", undefined, response.status)
}

export const searchLinkableRecords = (params: {
  q?: string
  entity_type?: "all" | "project" | "lead"
  id?: string
}) =>
  unwrap<Paged<LinkableRecord>>(
    request.api.get(`${BASE_URL}/linkable-records/`, params),
    "Search failed"
  )

export const listProjectTypes = () =>
  unwrap<ProjectTypeOption[]>(
    request.api.get(`${BASE_URL}/project-types/`),
    "Could not load project types"
  )

// Subcontractor contracts: added to the Complete Plan, or on their own once it is on the project.
const subPath = (id: string, documentId: string, rest = "") =>
  path(id, `subcontracts/${documentId}/${rest}`)

export const listSubcontracts = (id: string) =>
  unwrap<SubcontractsState>(
    request.api.get(path(id, "subcontracts/")),
    "Could not load the subcontractor contracts"
  )

export const uploadSubcontract = (id: string, file: File) => {
  const data = new FormData()
  data.append("file", file)
  return unwrap<SubcontractsState>(
    request.api.post(path(id, "subcontracts/"), data, MULTIPART),
    "Upload failed"
  )
}

export const deleteSubcontract = (id: string, documentId: string) =>
  unwrap<SubcontractsState>(
    request.api.delete(subPath(id, documentId)),
    "Could not remove the contract"
  )

export const replaceSubcontract = (
  id: string,
  documentId: string,
  file: File
) => {
  const data = new FormData()
  data.append("file", file)
  return unwrap<SubcontractsState>(
    request.api.post(subPath(id, documentId, "replace/"), data, MULTIPART),
    "Could not replace the file"
  )
}

export const excludeSubcontract = (id: string, documentId: string) =>
  unwrap<SubcontractsState>(
    request.api.post(subPath(id, documentId, "exclude/")),
    "Could not exclude the agreement"
  )

export const retrySubcontract = (id: string, documentId: string) =>
  unwrap<SubcontractsState>(
    request.api.post(subPath(id, documentId, "retry/")),
    "Could not read the contract again"
  )

export const patchSubcontractPayment = (
  id: string,
  documentId: string,
  key: string,
  body: SubcontractPaymentPatch
) =>
  unwrap<SubcontractsState>(
    request.api.patch(subPath(id, documentId, `payments/${key}/`), body),
    "Could not save"
  )

export const addSubcontractPayment = (
  id: string,
  documentId: string,
  body: SubcontractPaymentPatch
) =>
  unwrap<SubcontractsState>(
    request.api.post(subPath(id, documentId, "payments/"), body),
    "Could not add the payment"
  )

export const deleteSubcontractPayment = (
  id: string,
  documentId: string,
  key: string
) =>
  unwrap<SubcontractsState>(
    request.api.delete(subPath(id, documentId, `payments/${key}/`)),
    "Could not remove the payment"
  )

export const chooseSubcontractor = (
  id: string,
  documentId: string,
  subcontractorId: string
) =>
  unwrap<SubcontractsState>(
    request.api.put(subPath(id, documentId, "subcontractor/"), {
      subcontractor_id: subcontractorId
    }),
    "Could not choose the subcontractor"
  )

export const addSubcontractor = (
  id: string,
  documentId: string,
  body: { name: string; email: string; phone: string }
) =>
  unwrap<SubcontractsState>(
    request.api.post(subPath(id, documentId, "subcontractor/"), body),
    "Could not add the subcontractor"
  )

export const addSubcontractToPlan = (id: string, documentId: string) =>
  unwrap<SubcontractsState>(
    request.api.post(subPath(id, documentId, "plan/")),
    "Could not add the contract to the plan"
  )

export const takeSubcontractOutOfPlan = (id: string, documentId: string) =>
  unwrap<SubcontractsState>(
    request.api.delete(subPath(id, documentId, "plan/")),
    "Could not take the contract out of the plan"
  )

export const applySubcontract = (id: string, documentId: string) =>
  unwrap<SubcontractsState>(
    request.api.post(subPath(id, documentId, "apply/")),
    "Could not add the payables"
  )

export const searchSubcontractors = (q: string) =>
  unwrap<SubcontractorRef[]>(
    request.api.get(`${BASE_URL}/subcontractors/`, q ? { q } : {}),
    "Could not load subcontractors"
  )
